/**
 * LazySignaturePad
 * ────────────────
 * Drop-in replacement for `<SignatureCanvas ref={...} ... />`.
 *
 * Why this exists: the `react-signature-canvas` library is 30+ KB
 * gzipped and was being eagerly imported at the top of App.js even
 * though it is only used inside 3 signature dialogs that 90 % of
 * sessions never open.  React.lazy() forwards refs in React 18+
 * but its semantics around `useImperativeHandle` and ref methods
 * (`.clear()`, `.toDataURL()`, `.isEmpty()`) are fragile when the
 * lazy component hasn't resolved yet — a caller invoking
 * `sigPadRef.current.clear()` immediately after mount would NPE.
 *
 * Solution: wrap the dynamic `import('react-signature-canvas')` in
 * a tiny component that:
 *   1. Loads the library on mount (parallel with the surrounding
 *      dialog opening).
 *   2. Exposes a ref-like API via `useImperativeHandle` so the
 *      existing call sites (`sigPadRef.current.clear()`,
 *      `.toDataURL('image/png')`, `.isEmpty()`) keep working
 *      verbatim.
 *   3. Defers ref-method calls until the library has resolved.
 *
 * Result: react-signature-canvas downloads only when a signature
 * dialog opens, not at app boot.  All 3 existing call sites in
 * App.js work unchanged — they just `import LazySignaturePad from
 * '@/components/LazySignaturePad'` instead of
 * `import SignatureCanvas from 'react-signature-canvas'`.
 */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

const LazySignaturePad = forwardRef(function LazySignaturePad(props, ref) {
  // Holds the resolved component class.
  const [Component, setComponent] = useState(null);
  // The real SignatureCanvas instance (populated after the lazy
  // module has loaded and the inner ref attaches).
  const innerRef = useRef(null);
  // Calls that arrived before the library loaded — replay on resolve.
  const pendingCalls = useRef([]);
  // Cancel flag for the in-flight import (in case the dialog
  // closes before the chunk arrives).
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    import('react-signature-canvas')
      .then((mod) => {
        if (cancelled.current) return;
        setComponent(() => mod.default);
      })
      .catch((err) => {
        // Network failure on chunk fetch — log and let the caller
        // see the empty fallback below.
        console.error('[LazySignaturePad] chunk load failed:', err);
      });
    return () => {
      cancelled.current = true;
    };
  }, []);

  // Expose a ref-shaped API to the parent.  All methods proxy to
  // the inner SignatureCanvas once it has attached.
  useImperativeHandle(ref, () => ({
    clear() {
      if (innerRef.current?.clear) {
        innerRef.current.clear();
      } else {
        pendingCalls.current.push(['clear', []]);
      }
    },
    toDataURL(type, encoderOptions) {
      if (innerRef.current?.toDataURL) {
        return innerRef.current.toDataURL(type, encoderOptions);
      }
      // Library not yet loaded — return null; callers should
      // check `isEmpty()` first.
      return null;
    },
    isEmpty() {
      if (innerRef.current?.isEmpty) {
        return innerRef.current.isEmpty();
      }
      // Pre-load: act as if empty so save handlers refuse to
      // submit a blank signature.
      return true;
    },
    fromDataURL(dataURL, options) {
      if (innerRef.current?.fromDataURL) {
        innerRef.current.fromDataURL(dataURL, options);
      } else {
        pendingCalls.current.push(['fromDataURL', [dataURL, options]]);
      }
    },
    // Direct access to the underlying instance for any callers
    // that need methods we haven't proxied yet.
    getInstance() {
      return innerRef.current;
    },
  }), []);

  // When the library loads, drain any queued calls so callers that
  // ran `ref.current.clear()` before the chunk arrived still see
  // their effect applied.
  const onInnerRef = (instance) => {
    innerRef.current = instance;
    if (instance && pendingCalls.current.length > 0) {
      pendingCalls.current.forEach(([method, args]) => {
        if (typeof instance[method] === 'function') {
          instance[method](...args);
        }
      });
      pendingCalls.current = [];
    }
  };

  if (!Component) {
    // Visible placeholder so users see SOMETHING while the chunk
    // arrives (sub-second on a hot cache, ~200-500 ms on cold).
    return (
      <div
        className="flex items-center justify-center w-full h-48 bg-gray-50 text-gray-400 text-sm rounded"
        data-testid="signature-pad-loading"
      >
        Loading signature pad…
      </div>
    );
  }

  return <Component ref={onInnerRef} {...props} />;
});

export default LazySignaturePad;
